import { computed, effect, inject } from '@angular/core';
import { SelectedSurveyMarkDto } from '@core/api/angular';
import { groupBy } from '@lassi/util';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { LatLngBounds } from 'leaflet';
import { catchError, debounceTime, map, of, pipe, switchMap, tap } from 'rxjs';
import { FeatureService } from './feature.service';
import { createOverlayLayer, MapService } from './map';
import { isPointGeometryInMGABoundary } from './map/coordinate.utils';
import { isIOS } from './platform';
import { ToastService } from './toast/toast.service';

const MAX_SELECTED_SURVEY_MARKS = 250;

export enum SelectSurveyMarkStatus {
  INITIAL,
  LOADING,
  SUCCESS,
  ERROR,
}

export interface SelectSurveyMarksParams {
  bounds: LatLngBounds;
  action: 'add' | 'remove';
}

export interface SelectSurveyMarkState {
  items: SelectedSurveyMarkDto[];
  status: SelectSurveyMarkStatus;
  error: string | null;
  isSelecting: boolean;
}

export const SelectSurveyMarkStore = signalStore(
  {
    providedIn: 'root',
  },
  withState<SelectSurveyMarkState>({
    items: [],
    status: SelectSurveyMarkStatus.INITIAL,
    error: null,
    isSelecting: false,
  }),
  withComputed((store) => ({
    total: computed(() => store.items().length),
    isLoading: computed(
      () => store.status() === SelectSurveyMarkStatus.LOADING,
    ),
    anyIsWithinBoundary: computed(() =>
      store.items().some((mark) => mark.in_boundary),
    ),
  })),
  withMethods(
    (
      store,
      featureService = inject(FeatureService),
      toast = inject(ToastService),
    ) => {
      const handleSurveyMarksResult = (
        surveyMarks: SelectedSurveyMarkDto[],
        action: 'add' | 'remove',
      ) => {
        if (action === 'add') {
          addSurveyMarks(surveyMarks);
        } else if (action === 'remove') {
          removeSurveyMarks(surveyMarks.map((mark) => mark.mark_id));
        }
      };

      const addSurveyMarks = (surveyMarks: SelectedSurveyMarkDto[]) => {
        patchState(store, (state) => {
          if (store.total() + surveyMarks.length > MAX_SELECTED_SURVEY_MARKS) {
            toast.showErrorMessage(
              `You can only select up to ${MAX_SELECTED_SURVEY_MARKS} survey marks`,
            );
            return {
              status: SelectSurveyMarkStatus.SUCCESS,
            };
          }

          const existingItemsMap = new Map(
            state.items.map((item) => [item.mark_id, item]),
          );
          const newSurveyMarks = surveyMarks.filter(
            (mark) => !existingItemsMap.has(mark.mark_id),
          );
          if (newSurveyMarks.length === 0) {
            return { status: SelectSurveyMarkStatus.SUCCESS };
          }

          toast.showSuccessMessage(
            `${newSurveyMarks.length} survey mark${newSurveyMarks.length > 1 ? 's' : ''} added`,
          );
          return {
            items: [...state.items, ...newSurveyMarks],
            status: SelectSurveyMarkStatus.SUCCESS,
            error: null,
          };
        });
      };

      const removeSurveyMarks = (
        surveyMarks: SelectedSurveyMarkDto['mark_id'][],
      ) => {
        patchState(store, (state) => {
          if (surveyMarks.length === 0) {
            return { status: SelectSurveyMarkStatus.SUCCESS };
          }
          const idsToRemove = new Set(surveyMarks);

          toast.showSuccessMessage(
            `${surveyMarks.length} survey mark${surveyMarks.length > 1 ? 's' : ''} removed`,
          );
          const remainingItems = state.items.filter(
            (mark) => !idsToRemove.has(mark.mark_id),
          );
          return {
            items: remainingItems,
            status: SelectSurveyMarkStatus.SUCCESS,
            error: null,
          };
        });
      };

      const handleError = (error: unknown) => {
        console.error('Error fetching survey marks:', error);
        patchState(store, {
          status: SelectSurveyMarkStatus.ERROR,
          error: 'Failed to fetch survey marks',
        });
        return of([]);
      };

      return {
        addSurveyMarks,
        removeSurveyMarks,
        setSurveyMarks(surveyMarks: SelectedSurveyMarkDto[]) {
          patchState(store, () => {
            if (surveyMarks.length > MAX_SELECTED_SURVEY_MARKS) {
              toast.showErrorMessage(
                `You can only select up to ${MAX_SELECTED_SURVEY_MARKS} survey marks`,
              );
              return {
                status: SelectSurveyMarkStatus.SUCCESS,
              };
            }

            toast.showSuccessMessage(
              `${surveyMarks.length} survey mark${surveyMarks.length > 1 ? 's' : ''} added`,
            );

            return {
              items: surveyMarks,
              status: SelectSurveyMarkStatus.SUCCESS,
              error: null,
            };
          });
        },
        clearSurveyMarks() {
          toast.showSuccessMessage(
            `${store.total()} survey mark${store.total() > 1 ? 's' : ''} removed`,
          );
          patchState(store, {
            items: [],
            status: SelectSurveyMarkStatus.INITIAL,
            error: null,
          });
        },
        setError(errorMessage: string) {
          patchState(store, {
            status: SelectSurveyMarkStatus.ERROR,
            error: errorMessage,
          });
        },
        setLoading() {
          patchState(store, {
            status: SelectSurveyMarkStatus.LOADING,
          });
        },
        // method to handle select mark from map
        selectSurveyMarks: rxMethod<SelectSurveyMarksParams>(
          pipe(
            debounceTime(100),
            tap(() => {
              patchState(store, {
                status: SelectSurveyMarkStatus.LOADING,
              });
            }),
            switchMap(({ bounds, action }) => {
              return featureService.getSurveyMarkFeatures(bounds).pipe(
                // Transform ISurveyMarkFeature to SelectedSurveyMarkDto
                map((surveyMarks) =>
                  surveyMarks.map(
                    (mark): SelectedSurveyMarkDto => ({
                      geometry: {
                        ...mark.geometry,
                        crs: mark.crs,
                      },
                      in_boundary: isPointGeometryInMGABoundary(mark.geometry),
                      mark_id: mark.id,
                      mark_name: mark.name,
                      layer_name: mark.layer_name,
                    }),
                  ),
                ),
                tap((surveyMarksDto) =>
                  handleSurveyMarksResult(surveyMarksDto, action),
                ),
                catchError((error) => handleError(error)),
              );
            }),
          ),
        ),
      };
    },
  ),
  withHooks(
    (
      store,
      mapService = inject(MapService),
      featureService = inject(FeatureService),
    ) => {
      const layers = featureService.queryLayerMap.surveyMark.map((layer) =>
        typeof layer === 'string' ? layer : layer.id,
      );

      // create a markLayer and a highlightLayer per survey mark layer
      // we do this instead of doing it in a single layer to avoid issues with having a url that's too long
      const overlayLayerMap = new Map(
        layers.map((layer) => {
          const markLayer = createOverlayLayer(layer, false, isIOS(), 999);

          const highlightLayer = createOverlayLayer(
            layer,
            false,
            isIOS(),
            998,
            'POINT_HIGHLIGHT',
          );

          return [layer, { markLayer, highlightLayer }];
        }),
      );

      return {
        onInit: () => {
          effect(() => {
            if (store.items().length === 0) {
              overlayLayerMap.forEach(({ markLayer, highlightLayer }) => {
                markLayer.removeFrom(mapService.selectedSurveyMarksLayer);
                highlightLayer.removeFrom(mapService.selectedSurveyMarksLayer);
              });
            }

            const items = groupBy(store.items(), (item) => item.layer_name);

            for (const layer of layers) {
              const overlayLayers = overlayLayerMap.get(layer);
              if (!overlayLayers) continue;

              const itemsForLayer = items[layer] ?? [];

              if (itemsForLayer.length === 0) {
                overlayLayers.markLayer.removeFrom(
                  mapService.selectedSurveyMarksLayer,
                );
                overlayLayers.highlightLayer.removeFrom(
                  mapService.selectedSurveyMarksLayer,
                );
                continue;
              }

              const ids = itemsForLayer.map((m) => `'${m.mark_id}'`).join(',');
              const cql_filter = `mark_id IN (${ids})`;

              [overlayLayers.highlightLayer, overlayLayers.markLayer].forEach(
                (layer) => {
                  layer.setParams({
                    cql_filter,
                  });

                  if (!mapService.selectedSurveyMarksLayer.hasLayer(layer)) {
                    layer.addTo(mapService.selectedSurveyMarksLayer);
                  }
                },
              );
            }
          });
        },
        onDestroy: () => {
          overlayLayerMap.forEach(({ markLayer, highlightLayer }) => {
            markLayer.remove();
            highlightLayer.remove();
          });
        },
      };
    },
  ),
);
